import React from 'react';
import { injectIntl } from 'react-intl';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Brain from 'lucide-react/dist/esm/icons/brain';
import MessageSquare from 'lucide-react/dist/esm/icons/message-square';
import { slidingToggleButtonNoBorder, slidingToggleGroupNoBorder } from '../SlidingToggleButton/noBorderStyles';
import { discLabel } from '../../constants/disc';

// Map the *currently selected* axes to a DISC letter. A partial selection (only
// one axis) or an empty selection yields '' — so the result callout never shows
// for an incomplete/empty choice. Mirrors getDiscFromTraits, kept inline so this
// generic component carries no dependency on the ChatGPT input helpers.
const discFromTraits = traits => {
  if (traits.outgoing && traits.logical) return 'D';
  if (traits.outgoing && traits.emotional) return 'I';
  if (traits.introverted && traits.emotional) return 'S';
  if (traits.introverted && traits.logical) return 'C';
  return '';
};

// Per-DISC accent used to colour the "Approach" callout so it reads "live" and
// matches the selected type. Same hue family as the app-wide DISC_COLORS
// (D coral / I gold / S green / C blue), but the accent is a deeper, readable
// shade and the background a soft tint of it.
const DISC_CALLOUT_COLORS = {
  D: { accent: '#e05a3c', bg: '#fdeee9' },
  I: { accent: '#c99414', bg: '#fdf6e1' },
  S: { accent: '#22a34a', bg: '#e8f8ed' },
  C: { accent: '#2f7fd1', bg: '#e8f2fc' }
};

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  headerIconBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    borderRadius: 6,
    backgroundColor: '#fff7ed',
    color: '#ea580c'
  },
  headerTitle: {
    fontSize: 13.5,
    fontWeight: 700,
    color: '#1e293b'
  },
  togglesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12
  },
  toggleGroup: {
    position: 'relative',
    backgroundColor: '#f1f5f9',
    borderRadius: '9999px !important',
    padding: 2,
    zIndex: 0,
    border: '1px solid #e2e8f0',
    display: 'inline-flex',
    overflow: 'hidden',
    ...slidingToggleGroupNoBorder,
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  toggleButton: {
    ...slidingToggleButtonNoBorder,
    borderRadius: '9999px !important',
    padding: '0 14px',
    fontWeight: 600,
    fontSize: 12,
    height: 28,
    minWidth: 80,
    color: '#64748b !important',
    textTransform: 'none !important',
    zIndex: 2,
    backgroundColor: 'transparent !important',
    transition: 'color 0.25s ease !important',
    WebkitTapHighlightColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent !important',
      border: 'none !important',
      borderColor: 'transparent !important',
      boxShadow: 'none'
    },
    [theme.breakpoints.down('sm')]: {
      flex: 1,
      minWidth: 0,
      padding: '0 6px',
      fontSize: 11
    }
  },
  selected: {
    color: '#ffffff !important',
    backgroundColor: 'transparent !important',
    '&:hover': {
      backgroundColor: 'transparent !important'
    }
  },
  slidingIndicator: {
    position: 'absolute',
    top: 2,
    bottom: 2,
    left: 2,
    width: 'calc(50% - 2px)',
    backgroundColor: '#3966f8',
    borderRadius: 9999,
    boxShadow: '0 2px 6px rgba(57, 102, 248, 0.2)',
    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease, visibility 0.2s ease',
    zIndex: 1
  },
  callout: {
    backgroundColor: '#f8fafc',
    borderLeft: '3px solid #64748b',
    borderRadius: '0 8px 8px 0',
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  calloutHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 700,
    color: '#475569'
  },
  calloutIcon: {
    color: '#64748b',
    flexShrink: 0
  },
  calloutBody: {
    fontSize: 13.5,
    fontWeight: 400,
    color: '#334155',
    lineHeight: 1.5
  }
}));

/**
 * Controlled DISC personality picker — two sliding-pill toggle groups
 * (outgoing/introverted, logical/emotional) plus an "Approach" result callout
 * shown once a complete DISC letter is chosen.
 *
 * Presentational only: the parent owns `traits` and persistence, and reacts to
 * `onToggleTrait(trait)` for each tap. The result is derived from `traits`, so a
 * partial or empty selection shows no callout.
 */
const DISCPersonalityPicker = ({ intl, traits = {}, onToggleTrait, showLabel = true, required = false, labelText }) => {
  const { classes } = useStyles();

  const group1Value = traits.outgoing ? 'outgoing' : traits.introverted ? 'introverted' : '';
  const group1Index = traits.outgoing ? 0 : traits.introverted ? 1 : -1;

  const group2Value = traits.logical ? 'logical' : traits.emotional ? 'emotional' : '';
  const group2Index = traits.logical ? 0 : traits.emotional ? 1 : -1;

  const handleToggleGroup1 = (event, value) => {
    onToggleTrait(value === null ? (traits.outgoing ? 'outgoing' : 'introverted') : value);
  };

  const handleToggleGroup2 = (event, value) => {
    onToggleTrait(value === null ? (traits.logical ? 'logical' : 'emotional') : value);
  };

  const disc = discFromTraits(traits);
  const approachKey = disc ? `${disc}_approach` : null;
  const calloutPalette = disc ? DISC_CALLOUT_COLORS[disc] : null;

  return (
    <div className={classes.root}>
      {showLabel && (
        <div className={classes.header}>
          <span className={classes.headerIconBox}>
            <Brain size={14} />
          </span>
          <span className={classes.headerTitle}>
            {labelText || intl.formatMessage({ id: 'choose-personality' })}
            {required ? ' *' : ''}
          </span>
        </div>
      )}

      <div className={classes.togglesContainer}>
        <ToggleButtonGroup
          value={group1Value}
          exclusive
          onChange={handleToggleGroup1}
          classes={{ root: classes.toggleGroup }}
        >
          <div
            className={classes.slidingIndicator}
            style={{
              transform: `translateX(${group1Index * 100}%)`,
              opacity: group1Index === -1 ? 0 : 1,
              visibility: group1Index === -1 ? 'hidden' : 'visible'
            }}
          />
          <ToggleButton value="outgoing" classes={{ root: classes.toggleButton, selected: classes.selected }}>
            {intl.formatMessage({ id: 'outgoing' })}
          </ToggleButton>
          <ToggleButton value="introverted" classes={{ root: classes.toggleButton, selected: classes.selected }}>
            {intl.formatMessage({ id: 'introverted' })}
          </ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={group2Value}
          exclusive
          onChange={handleToggleGroup2}
          classes={{ root: classes.toggleGroup }}
        >
          <div
            className={classes.slidingIndicator}
            style={{
              transform: `translateX(${group2Index * 100}%)`,
              opacity: group2Index === -1 ? 0 : 1,
              visibility: group2Index === -1 ? 'hidden' : 'visible'
            }}
          />
          <ToggleButton value="logical" classes={{ root: classes.toggleButton, selected: classes.selected }}>
            {intl.formatMessage({ id: 'logical' })}
          </ToggleButton>
          <ToggleButton value="emotional" classes={{ root: classes.toggleButton, selected: classes.selected }}>
            {intl.formatMessage({ id: 'emotional' })}
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {approachKey && (
        <div
          className={classes.callout}
          style={
            calloutPalette ? { backgroundColor: calloutPalette.bg, borderLeftColor: calloutPalette.accent } : undefined
          }
        >
          <div className={classes.calloutHeader} style={calloutPalette ? { color: calloutPalette.accent } : undefined}>
            <MessageSquare
              className={classes.calloutIcon}
              size={16}
              color={calloutPalette ? calloutPalette.accent : undefined}
            />
            <span>
              {discLabel(disc, intl.locale)} · {intl.formatMessage({ id: 'Approach' })}:
            </span>
          </div>
          <div className={classes.calloutBody}>{intl.formatMessage({ id: approachKey })}</div>
        </div>
      )}
    </div>
  );
};

export default injectIntl(DISCPersonalityPicker);
